import React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useCompany } from "@/context/CompanyContext";
import Seo from "@/components/Seo";
import Breadcrumbs from "@/components/Breadcrumbs";
import CtaBlock from "@/components/CtaBlock";
import { MapPin, ArrowRight } from "lucide-react";

export default function ServiceAreas() {
  const { company } = useCompany();
  const { data: locations = [] } = useQuery({ queryKey: ["areas"], queryFn: async () => (await api.get("/locations")).data });

  return (
    <>
      <Seo title="HVAC Service Areas on the Space Coast | Aquila Air & Heating" description="Aquila Air & Heating provides AC repair, installation, and HVAC service across Cocoa, Brevard County, and the Space Coast. Find your city." path="/service-areas/" />
      <section className="bg-navy text-white py-12 md:py-16" data-testid="areas-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="[&_*]:!text-slate-300"><Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "Service Areas" }]} /></div>
          <h1 className="mt-5 text-4xl sm:text-5xl font-extrabold tracking-tight">Service Areas</h1>
          <p className="mt-3 max-w-2xl text-lg text-slate-200">Proudly serving {company.primary_service_area || "the Space Coast"} and the surrounding Brevard County communities with fast, reliable HVAC service.</p>
        </div>
      </section>

      <section className="py-14 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {locations.map((l) => (
              <Link key={l.slug} to={`/service-areas/${l.slug}`} data-testid={`area-card-${l.slug}`} className="group bg-white border border-slate-200 rounded-lg overflow-hidden shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200">
                <div className="relative h-40 overflow-hidden">
                  <img src={l.image} alt={l.image_alt} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" loading="lazy" />
                  <div className="absolute inset-0 bg-gradient-to-t from-navy/70 to-transparent" />
                  <div className="absolute bottom-3 left-4 flex items-center gap-2 text-white font-heading font-bold text-xl"><MapPin className="h-5 w-5 text-launch" /> {l.city}, FL</div>
                </div>
                <div className="p-5">
                  <p className="text-sm text-slate-600 line-clamp-2">{l.intro}</p>
                  <span className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-launch group-hover:gap-2 transition-all">HVAC in {l.city} <ArrowRight className="h-4 w-4" /></span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
      <CtaBlock />
    </>
  );
}
